import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { assert } from '../../common/asserts/assert';
import { getFormattedErrorFromGraphQLError } from '../../common/error-handling/getFormattedErrorFromGraphQLError';
import { trackException } from '../../common/tracking/appInsight';
import type { AppDispatch } from '../../store/appDispatch';
import { fatalError } from '../authentication/authActions';
import { healthCheckFail } from './healthCheckActions';
import { typePolicies } from './typePolicies';

let apiToken: string | null = null;

export const setToken = (token: string | null) => {
  apiToken = token;
};

export const getToken = () => apiToken;

let errorCallback: AppDispatch = () => {
  assert(false, 'Error callback not set');
};

export const setErrorCallback = (f: AppDispatch) => {
  errorCallback = f;
};

const authLink = new SetContextLink(({ headers }) => {
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${getToken()}`,
      'x-client-code': 'FP_BOOK',
      'x-client-version': import.meta.env.REACT_APP_VERSION,
    },
  };
});

const httpLink = new HttpLink({
  credentials: 'include',
  uri: operation => '/graphql?operationName=' + operation.operationName,
});

const errorLink = new ErrorLink(({ operation, error }) => {
  const isAuthorizationError = getFormattedErrorFromGraphQLError(error)?.some(e =>
    e.message.includes('401 Unauthorized'),
  );

  if (isAuthorizationError) {
    errorCallback(fatalError('invalid_token', ''));
    return;
  }

  const networkError = ServerError.is(error) ? error : null;

  if (networkError) {
    trackException(networkError, {
      operationName: operation.operationName,
    });

    if (
      operation.operationName === 'HealthCheckQuery' ||
      operation.operationName === 'PingLastLogin'
    ) {
      errorCallback(healthCheckFail());
    }
  }
});

export const client = new ApolloClient({
  devtools: { name: 'FreightPortal' },
  assumeImmutableResults: true,
  dataMasking: false,
  cache: new InMemoryCache({ typePolicies }),
  defaultOptions: {
    query: {
      errorPolicy: 'all',
    },
    watchQuery: {
      errorPolicy: 'all',
    },
  },
  link: ApolloLink.from([authLink, errorLink, httpLink]),
});

export const refetchQueries = (queries: string[]) =>
  client.refetchQueries({
    include: queries,
  });
